import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];

  connectedCallback() {
    super.connectedCallback();

    this.refs.searchPageInput.form?.addEventListener('submit', this.#handleSubmit);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.searchPageInput.form?.removeEventListener('submit', this.#handleSubmit);
  }

  /**
   * @param {SubmitEvent} event
   */
  #handleSubmit = (event) => {
    event.preventDefault();

    const form = this.refs.searchPageInput.form;
    if (!form) return;

    const url = new URL(form.action, window.location.origin);
    const formData = new FormData(form);

    url.search = new URLSearchParams(/** @type {Record<string, string>} */ (Object.fromEntries(formData))).toString();
    url.searchParams.set('q', this.#normalizeSearchQuery(this.refs.searchPageInput.value));

    window.location.href = url.toString();
  };

  /**
   * Handles the click event on the clear button and submits an empty search.
   * This clears the search input and resubmits the form if the page is not
   * already in an empty state.
   */
  handleClearClick() {
    this.#submitEmptySearch();
  }

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
    }
  }, 100);

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = '';

    if (this.#isEmptyState()) return;

    searchInput.form?.submit();
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return queryParam.trim() === '';
  };

  /**
   * @param {string} value
   * @returns {string}
   */
  #normalizeSearchQuery(value) {
    return value.trim().toLowerCase();
  }
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
